export const PRODUCT_PROGRESS_SECTIONS = [
  {
    id: 'live',
    label: 'Live now',
    tone: 'success',
    items: [
      {
        title: 'EU CRA reporting and policy gates',
        status: 'Live',
        detail: 'CRA audit, portfolio scoring, dashboard visibility, and policy enforcement are all shipped.',
      },
      {
        title: 'Malware and typosquatting detection v2',
        status: 'Live',
        detail: 'Multi-signal package detection, portfolio visibility, and real package ecosystem coverage are in production.',
      },
      {
        title: 'SLSA provenance verification',
        status: 'MVP',
        detail: 'Attestation upload, digest matching, and provenance metadata are live; trust verification is still open.',
      },
      {
        title: 'Supplier trust portfolio',
        status: 'Live',
        detail: 'Snapshot-backed supplier trust scoring now combines malware, typosquatting, maintainer health, abandoned packages, reachability, and SLSA signals.',
      },
      {
        title: 'Maintainer anomaly detection',
        status: 'Live',
        detail: 'Anomaly scores, severity, repo tabs, and summary cards are visible in the product.',
      },
      {
        title: 'Reachability analysis',
        status: 'MVP',
        detail: 'Reachability results and VEX persistence are wired end-to-end, with hardening still to do.',
      },
      {
        title: 'CI/CD wizard, webhooks, and pricing flow',
        status: 'Live',
        detail: 'Adoption and commercial packaging surfaces are visible to users directly in the portal.',
      },
      {
        title: 'Evidence pack export',
        status: 'Live',
        detail: 'Repo details can now export one ZIP bundle with SBOM, CRA artifacts, remediation, reachability, provenance, supplier trust, audit log, and manifest.',
      },
    ],
  },
  {
    id: 'next',
    label: 'Next up',
    tone: 'info',
    items: [
      {
        todoId: 'future-rascal-ir',
        title: 'Define rule IR contract',
        status: 'Next',
        detail: 'Define the stable internal rule representation that keeps Rascal behind a product-safe abstraction.',
      },
      {
        todoId: 'future-rascal-preview',
        title: 'Design rule preview pipeline',
        status: 'Next',
        detail: 'Preview rule outcomes safely before rollout so teams can understand impact before enforcement.',
      },
      {
        todoId: 'future-rascal-ai-assistant',
        title: 'Design AI rule assistant',
        status: 'Next',
        detail: 'Design an assistant that helps users create and refine rules without hand-writing everything.',
      },
      {
        todoId: 'future-rascal-community',
        title: 'Design community rule publishing',
        status: 'Next',
        detail: 'Let useful rules be shared and reused instead of staying trapped inside a single instance.',
      },
    ],
  },
  {
    id: 'later',
    label: 'Strategic gaps',
    tone: 'muted',
    items: [
      {
        title: 'SSO / SAML / Azure AD',
        status: 'Planned',
        detail: 'Still one of the clearest blockers for larger enterprise rollouts.',
      },
      {
        title: 'Jira / ServiceNow integration',
        status: 'Planned',
        detail: 'Remediation workflow should connect to the systems teams already use every day.',
      },
      {
        title: 'Customer website and searchable knowledge hub',
        status: 'Planned',
        detail: 'A public-facing DepGuard site for company context, product pages, docs, FAQ, and search is still missing.',
      },
      {
        title: 'IaC and container SBOM support',
        status: 'Planned',
        detail: 'Broaden coverage beyond application dependencies into deployment infrastructure.',
      },
      {
        title: 'PostgreSQL and multi-tenant hardening',
        status: 'Planned',
        detail: 'Needed to move from strong demo product to scalable SaaS-grade platform.',
      },
      {
        title: 'Autonomous AI security agent',
        status: 'Planned',
        detail: 'Still strategically important, but not presented as already shipped.',
      },
    ],
  },
];

export const PROJECT_TRACKS = [
  {
    id: 'depguard-api',
    name: 'DepGuard API',
    status: 'Active',
    summary: 'Backend project for SBOM processing, security analysis, policy enforcement, and integration APIs.',
    highlights: [
      'CRA reporting, supplier trust aggregation, malware detection, maintainer anomalies, and policy gates are implemented on the backend.',
      'SLSA provenance, reachability, and evidence-pack export endpoints are live as shipped platform capabilities.',
      'Next backend blockers are broader identity coverage, ServiceNow-style workflow integrations, and scalability hardening.',
    ],
  },
  {
    id: 'depguard-frontend',
    name: 'DepGuard portal frontend',
    status: 'Active',
    summary: 'Frontend portal project for dashboards, repo details, roadmap visibility, and user workflows.',
    highlights: [
      'Roadmap, Product Progress, pricing, supplier-trust, anomalies, SLSA, reachability, and evidence-pack flows are visible in the portal.',
      'The recent SBOM diff null crash is fixed and regression-tested.',
      'Next UX/commercial work is clearer execution tracking, a customer-facing website/knowledge hub, packaging, and enterprise workflow polish.',
    ],
  },
  {
    id: 'depguard-reach',
    name: 'depguard-reach',
    status: 'Building',
    summary: 'Separate reachability engine project that powers deeper exploitability and VEX decisions.',
    highlights: [
      'Foundation module, CLI, shared models, and test baseline are in place.',
      'Go analyzer, OSV function mapping, and CycloneDX VEX emission are implemented.',
      'Next work is orchestrator wiring, broader language analyzers, and a stronger DepGuard bridge.',
    ],
  },
  {
    id: 'depguard-nexus',
    name: 'DepGuard Nexus',
    status: 'Planned',
    summary: 'Separate future intelligence engine for repository and code analysis, designed to feed the platform without leaking internal engine details into the product.',
    highlights: [
      'Saved plans define Nexus as the standalone analysis engine, separate from the Platform roadmap.',
      'Its roadmap owns analyzers, fact and graph models, scoring, and AI-grounded evidence internals.',
      'The plan keeps Nexus behind stable contracts so Platform can consume outputs without depending on Nexus internals.',
    ],
  },
  {
    id: 'depguard-rules',
    name: 'depguard-rules',
    status: 'Planned',
    summary: 'Future open-source query and rule-pack project that supplies reusable knowledge packs to Nexus.',
    highlights: [
      'Saved plans define this as a separate knowledge layer rather than part of the main Platform codebase.',
      'The strongest stored naming direction is `depguard-rules`.',
      'The open saved workstreams currently tied to this direction are `future-rascal-ir`, `future-rascal-preview`, `future-rascal-ai-assistant`, and `future-rascal-community`.',
    ],
  },
  {
    id: 'validation-corpus',
    name: 'Open-source validation corpus',
    status: 'Ready',
    summary: 'Operational validation track used to prove the product against real public repositories.',
    highlights: [
      'Batch 1 is defined as the first golden baseline across six pinned repositories.',
      'Generation, import, SLSA upload, and reachability scripts all exist and have been exercised end-to-end.',
      'This gives us a repeatable regression path for future roadmap work.',
    ],
  },
];

export const RECENTLY_DELIVERED = [
  {
    title: 'Evidence pack export shipped',
    detail: 'Teams can now download one audit-friendly ZIP per SBOM with CRA, remediation, reachability, provenance, supplier trust, audit log, and manifest data.',
  },
  {
    title: 'Supplier trust portfolio surfaced',
    detail: 'Portfolio-level supplier trust scoring is now visible as a first-class cross-repository signal instead of hidden implementation detail.',
  },
  {
    title: 'Commercial roadmap synced with reality',
    detail: 'The portal roadmap now reflects what is actually shipped versus what remains MVP or planned.',
  },
  {
    title: 'Corpus baseline established',
    detail: 'The first open-source validation corpus now runs generation, import, SLSA, and reachability end-to-end.',
  },
  {
    title: 'Maintainer anomalies surfaced',
    detail: 'Teams can now inspect anomaly findings directly in repo details instead of digging through raw data.',
  },
];

export function getProductProgressMetrics() {
  const live = PRODUCT_PROGRESS_SECTIONS.find((section) => section.id === 'live')?.items || [];
  const next = PRODUCT_PROGRESS_SECTIONS.find((section) => section.id === 'next')?.items || [];
  const later = PRODUCT_PROGRESS_SECTIONS.find((section) => section.id === 'later')?.items || [];
  const mvp = live.filter((item) => item.status === 'MVP').length;

  return {
    projects: PROJECT_TRACKS.length,
    live: live.length,
    mvp,
    next: next.length,
    later: later.length,
    recent: RECENTLY_DELIVERED.length,
  };
}
